import { resolvePromptGroups } from '@api/narratives/utilities/helpers';

/**
 * Transforms prompt answers for API submission.
 * @param {Array} promptAnswers - The array of prompt answers to transform.
 * @returns {Array} Transformed prompt answers ready for API submission.
 */
export const transformPromptAnswers = (promptAnswers) => {
  const preparedAnswers = promptAnswers.reduce((acc, { field, values }) => {
    if (Array.isArray(values)) {
      if (acc[field]) {
        acc[field] = [...acc[field], ...values];
      } else {
        acc[field] = values;
      }
    } else if (typeof values === 'object' && values.date_range) {
      acc[field] = values.date_range;
    }
    return acc;
  }, {});

  return {
    ...preparedAnswers,
  };
};

const createGroupedPrompt = ({ groupedPrompts, groupingId, groupingIndex, groupingName }) => ({
  attributes: groupedPrompts.flatMap((prompt) => prompt.attributes || []),
  description: null,
  emptyValue: `Any ${groupingName || 'Prompt'}`,
  groupedPrompts,
  hasCustomDateOption: false,
  id: groupingId || `prompt_grouping_${groupingIndex}`,
  isAdvanced: false,
  isDateRange: false,
  isPinnable: true,
  isPrefixIncluded: true,
  isReadOnly: false,
  isRequired: false,
  isSearchEnabled: true,
  isSortEnabled: false,
  maxValues: null,
  minAnswers: null,
  name: groupingName || '',
  promptKey: groupingId || `prompt_grouping_${groupingIndex}`,
  shortTitle: `Select ${groupingName || ''}`,
  title: `Select ${groupingName || ''}`,
  type: 10,
});

/** * Filters prompts based on the narrative ID.
 * @param {Object[]} prompts - The array of prompts to filter.
 * @param {Object} promptGrouping - The prompt grouping object containing prompt name and grouped prompt IDs.
 * @returns {Object[]} Filtered prompts.
 */
export const filterPrompts = (prompts, promptGrouping) => {
  if (!Array.isArray(prompts)) return [];
  const getPromptKey = (prompt) => prompt?.promptKey || prompt?.id;
  const isVisiblePrompt = (prompt) => prompt?.isAdvanced !== true;

  const visiblePrompts = prompts.filter(isVisiblePrompt);
  const promptByKey = new Map(prompts.map((prompt) => [getPromptKey(prompt), prompt]));
  const groupedPromptKeys = new Set();
  const groupedPrompts = resolvePromptGroups(prompts, promptGrouping).map(
    ({ group, groupIndex, promptKeys }) => {
      promptKeys.forEach((promptKey) => groupedPromptKeys.add(promptKey));
      return createGroupedPrompt({
        groupedPrompts: promptKeys.map((promptKey) => promptByKey.get(promptKey)),
        groupingId: group?.id,
        groupingIndex: groupIndex,
        groupingName: group?.name,
      });
    }
  );

  if (!groupedPromptKeys.size) return visiblePrompts;

  const groupedPromptByFirstKey = new Map(
    groupedPrompts.map((groupedPrompt) => [
      getPromptKey(groupedPrompt.groupedPrompts[0]),
      groupedPrompt,
    ])
  );

  return prompts.reduce((result, prompt) => {
    const promptKey = getPromptKey(prompt);
    const groupedPrompt = groupedPromptByFirstKey.get(promptKey);
    if (groupedPrompt) result.push(groupedPrompt);
    else if (isVisiblePrompt(prompt) && !groupedPromptKeys.has(promptKey)) result.push(prompt);
    return result;
  }, []);
};
